import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@cyphex/ui';
import type { ConversationId, CreateReminderRequest, Message } from '@cyphex/types';
import { chatApi } from '../api';
import { CHAT_KEYS } from '../constants';
import { patchMessage } from '../lib/timelineCache';

export interface ReminderActions {
  set: (message: Message, request: CreateReminderRequest) => void;
  clear: (message: Message) => void;
}

/**
 * Arm / disarm a reminder.
 *
 * `reminderAt` on the message is what draws the bell on the bubble, so it is
 * patched optimistically and rolled back on failure. The optimistic value for a
 * preset is only an ESTIMATE — "tomorrow morning" is resolved server-side in
 * the user's timezone — so the response overwrites it as soon as it lands.
 */
export function useReminderActions(conversationId: ConversationId | null): ReminderActions {
  const client = useQueryClient();

  const set = useCallback(
    (message: Message, request: CreateReminderRequest) => {
      if (!conversationId) return;
      const previous = message.reminderAt;
      const optimistic =
        request.remindAt ?? new Date(Date.now() + (request.minutes ?? 60) * 60_000).toISOString();
      patchMessage(client, conversationId, message.id, (current) => ({
        ...current,
        reminderAt: optimistic,
      }));

      void chatApi
        .setReminder(conversationId, message.id, request)
        .then((saved) => {
          patchMessage(client, conversationId, message.id, (current) => ({
            ...current,
            reminderAt: saved.remindAt,
          }));
          void client.invalidateQueries({ queryKey: CHAT_KEYS.reminders() });
          toast.success('Reminder set.');
        })
        .catch(() => {
          patchMessage(client, conversationId, message.id, (current) => ({
            ...current,
            reminderAt: previous,
          }));
          toast.error('Could not set that reminder.');
        });
    },
    [client, conversationId],
  );

  const clear = useCallback(
    (message: Message) => {
      if (!conversationId) return;
      const previous = message.reminderAt;
      patchMessage(client, conversationId, message.id, (current) => ({
        ...current,
        reminderAt: null,
      }));
      void chatApi
        .clearReminder(conversationId, message.id)
        .then(() => client.invalidateQueries({ queryKey: CHAT_KEYS.reminders() }))
        .catch(() => {
          patchMessage(client, conversationId, message.id, (current) => ({
            ...current,
            reminderAt: previous,
          }));
          toast.error('Could not clear that reminder.');
        });
    },
    [client, conversationId],
  );

  return useMemo(() => ({ set, clear }), [clear, set]);
}
